<template>
    <section>

        <div class="title">
            Classic Minesweeper
        </div>

        <div class="columns is-size-2-desktop is-size-3-touch">
            <div class="column is-one-fifth">
            </div>

            <div class="column field is-grouped">
                <div class="mr-5">{{ formattedTime }}</div>
                <div><i class="fas fa-bomb"></i> {{ minesRemaining }}</div>
            </div>

            <div class="column field is-grouped">
                <div class="buttons has-addons">
                    <button @click="setDifficulty('easy')" class="button">Easy</button>
                    <button @click="setDifficulty('medium')" class="button">Medium</button>
                    <button @click="setDifficulty('hard')" class="button">Hard</button>
                </div>
                <button @click="toggleMobile()" id="mobile-toggle" class="button ml-2"><i class="fas fa-mobile-alt is-size-4"></i></button>
            </div>

            <div class="column is-one-fifth">
            </div>
        </div>

        <div>
            <div id="board">
                <div v-for="(cell, index) in cells" :key="index" class="cellwrapper">
                    <div
                        @click="reveal(index)"
                        @contextmenu.prevent="cycleState(index)"
                        :id="`cell${index}`"
                        class="cell is-size-2-fullhd is-size-3-desktop is-size-3-tablet is-size-5-mobile">
                            <span v-if="cell.state === 0 || cell.state === 1">
                                <span v-if="cell.value === -1"><i class="fas fa-bomb"></i></span>
                                <span v-else>{{ cell.value }}</span>
                            </span>
                            <span v-else-if="cell.state === 2"><i class="fab fa-font-awesome-flag"></i></span>
                            <span v-else-if="cell.state === 3"><i class="fas fa-question"></i></span>
                    </div>
                </div>
            </div>
        </div>

        <button @click="reset()" id="reset-button" class="button is-large">Play Again!</button>

    </section>
</template>

<script>
export default {
    name: 'Minesweeper',
    data() {
        return {
            cells: [],
            time: { clock: null, live: false, display: "00:00", minutes: 0, seconds: 0 },
            grid: { width: 14, height: 10, mines: 28, size: 7, delay: 80 },
            game: { disabled: false, difficulty: 'medium', mobile: false },
            minePositions: [],
            minesRemaining: 0
        }
    },
    computed: {
        formattedTime: function() {
            const minutes = this.time.minutes < 10 ? "0"+this.time.minutes : this.time.minutes.toString();
            const seconds = this.time.seconds < 10 ? "0"+this.time.seconds : this.time.seconds.toString();
            return minutes+':'+seconds;
        }
    },
    mounted() {
        this.formatGame();
    },
    methods: {
        allocateMines: function() {
            this.minePositions = [];
            for (let i = 0; i < this.grid.mines; i++) {
                let mine = Math.floor( Math.random() * this.cells.length );
                if (this.cells[mine].value < 0) {
                    i--; //already a mine here or cell is protected, loop an additional time
                } else {
                    this.cells[mine].value = -1;
                    this.minePositions.push(mine);
                }
            }
            this.minePositions.sort((a, b) => a - b);
            return;
        },
        calculateSafeCells: async function() {
            for (let i = 0; i < this.cells.length; i++) {
                if (this.cells[i].value !== -1) { //if cell is a mine, skip
                    let value = 0;
                    let adjacents = await this.getAdjacentsOf(i);
                    adjacents.forEach( adjacent => {
                        if (adjacent !== null && adjacent.value === -1) value++;
                    });
                    this.cells[i].value = value;
                }
            }
            return;
        },
        createCells: function() {
            this.cells = [];
            for (let i = 0; i < this.grid.height; i++) {
                for (let j = 0; j < this.grid.width; j++) {
                    this.cells.push({
                        index: (i*this.grid.width)+(j%this.grid.width),
                        value: 0,
                        state: 1
                    });
                }
            }
        },
        cycleState: function(cell) {
            if (this.game.disabled) return;
            switch(this.cells[cell].state) {
                case 0: return;
                case 1: this.cells[cell].state++; this.paint(cell, 'mediumspringgreen', 'sandybrown', 'black'); break;
                case 2: this.cells[cell].state++; this.paint(cell, 'mediumspringgreen', 'sandybrown', 'black'); break;
                case 3: this.cells[cell].state=1; this.paint(cell, 'mediumspringgreen', 'sandybrown', 'transparent'); break;
            }
            this.updateMinesRemaining();
        },
        declareWinner: async function() {
            this.timer('stop');
            this.game.disabled = true;
            for (const mine of this.minePositions) {
                await this.sleep(this.grid.delay*2);
                this.cells[mine].state = 2;
                this.paint(mine, 'violet', 'whitesmoke', 'lavenderblush');
            }
            document.getElementById("reset-button").style.visibility = "visible";
        },
        emptyGrid: function() {
            for (let i = 0; i < this.cells.length; i++) {
                this.cells[i].index = i;
                this.cells[i].value = 0;
                this.cells[i].state = 1;
                this.paint(i, 'mediumspringgreen', 'sandybrown', 'transparent');
            }
            return;
        },
        formatGame: async function() {
            this.timer('stop');
            switch(this.game.difficulty) {
                default: // default to medium
                case 'medium': this.grid = { width: 14, height: 10, mines: 26, size: 7, delay: 80 }; break;
                case 'easy': this.grid = { width: 10, height: 8, mines: 10, size: 8, delay: 160 }; break;
                case 'hard': this.grid = { width: 18, height: 12, mines: 48, size: 6, delay: 40 }; break;
            }
            if (this.game.mobile) {
                [this.grid.width, this.grid.height] = [this.grid.height, this.grid.width];
                this.grid.size = Math.floor(100/this.grid.width);
            }
            await this.createCells();
            document.getElementById('board').style.gridTemplateColumns = `repeat(${this.grid.width}, ${this.grid.size}vmin)`;
            document.getElementById('board').style.gridTemplateRows = `repeat(${this.grid.height}, ${this.grid.size}vmin)`;
            
            this.timer('reset');
            this.minesRemaining = this.grid.mines;
            await this.emptyGrid();
            return;
        },
        gameOver: async function(cell) {
            this.timer('stop');
            this.game.disabled = true;
            this.paint(cell, 'firebrick', 'black', 'black');
            for (const mine of this.minePositions) {
                await this.sleep(this.grid.delay);
                this.paint(mine, 'firebrick', 'black', 'black');
            }
            document.getElementById("reset-button").style.visibility = "visible";
        },
        gameWon: function() {
            let gameWon = true;
            this.cells.forEach( cell => {
                if (cell.value !== -1 && cell.state !== 0) {
                    gameWon = false;
                }
            });
            return gameWon;
        },
        getAdjacentsOf: function(cell) {
            let adjacents = [];
            let isNorth = false, isEast = false, isSouth = false, isWest = false;

            if (cell-this.grid.width >= 0) {  //check North cell
                const adjacent = cell-this.grid.width;
                adjacents.push({ index: adjacent, value: this.cells[adjacent].value, state: this.cells[adjacent].state });
            } else {
                isNorth = true;
                adjacents.push(null);
            }

            if (cell%this.grid.width < this.grid.width-1) {  // check East cell
                const adjacent = cell+1;
                adjacents.push({ index: adjacent, value: this.cells[adjacent].value, state: this.cells[adjacent].state });
            } else {
                isEast = true;
                adjacents.push(null);
            }

            if (cell+this.grid.width < this.cells.length) {  //check South cell
                const adjacent = cell+this.grid.width;
                adjacents.push({ index: adjacent, value: this.cells[adjacent].value, state: this.cells[adjacent].state });
            } else {
                isSouth = true;
                adjacents.push(null);
            }

            if (cell%this.grid.width > 0) {  //Check West cell
                const adjacent = cell-1;
                adjacents.push({ index: adjacent, value: this.cells[adjacent].value, state: this.cells[adjacent].state });
            } else {
                isWest = true;
                adjacents.push(null);
            }

            if (!isNorth && !isEast) { //Check NE cell
                const adjacent = cell-this.grid.width+1;
                adjacents.splice(1, 0, { index: adjacent, value: this.cells[adjacent].value, state: this.cells[adjacent].state });
            } else {
                adjacents.splice(1, 0, null);
            }

            if (!isSouth && !isEast) { //Check SE cell
                const adjacent = cell+this.grid.width+1;
                adjacents.splice(3, 0, { index: adjacent, value: this.cells[adjacent].value, state: this.cells[adjacent].state });
            } else {
                adjacents.splice(3, 0, null);
            }

            if (!isSouth && !isWest) { //check SW cell
                const adjacent = cell+this.grid.width-1;
                adjacents.splice(5, 0, { index: adjacent, value: this.cells[adjacent].value, state: this.cells[adjacent].state });
            } else {
                adjacents.splice(5, 0, null);
            }
            if (!isNorth && !isWest) { //check NW cell
                const adjacent = cell-this.grid.width-1;
                adjacents.splice(7, 0, { index: adjacent, value: this.cells[adjacent].value, state: this.cells[adjacent].state });
            } else {
                adjacents.splice(7, 0, null);
            }

            return adjacents;
        },
        initialiseGame: async function() {
            await this.allocateMines();
            await this.calculateSafeCells();
            this.updateMinesRemaining();
            this.game.disabled = false;
        },
        lookupColor: function(value) {
            switch (value) {
                case 0: return "transparent";
                case 1: return "dodgerblue";
                case 2: return "forestgreen";
                case 3: return "firebrick";
                case 4: return "blueviolet";
                case 5: return "maroon";
                case 6: return "lightseagreen";
                case 7: return "black";
                case 8: return "dimgray";
            }
        },
        paint: function(cell, background, boarder, color) {
            document.getElementById(`cell${cell}`).style.background = background;
            document.getElementById(`cell${cell}`).style.borderColor = boarder;
            document.getElementById(`cell${cell}`).style.color = color;
            return;
        },
        reset: async function() {
            document.getElementById("reset-button").style.visibility = "hidden";
            this.timer('reset');
            this.minesRemaining = this.grid.mines;
            await this.emptyGrid();
            this.game.disabled = false;
            return;
        },
        reveal: async function(cell) {
            if (this.game.disabled || this.cells[cell].state !== 1) return;
            if (!this.time.live) {
                const adjacents = await this.getAdjacentsOf(cell);
                adjacents.forEach( adjacent => {
                    if (adjacent !== null) this.cells[adjacent.index].value = -2;
                });
                this.cells[cell].value = -2;
                await this.initialiseGame();
                this.timer('start');
                this.reveal(cell);
                return;
            }

            if (this.cells[cell].value === -1) {
                this.gameOver(cell);
                return;
            }

            this.paint(cell, 'lightgrey', 'darkgrey', this.lookupColor(this.cells[cell].value) );
            this.cells[cell].state = 0;

            if (this.cells[cell].value === 0) {
                const adjacents = await this.getAdjacentsOf(cell);
                adjacents.forEach( adjacent => {
                    if (adjacent !== null) this.reveal(adjacent.index);
                });
            }

            if (this.gameWon()) this.declareWinner();
        },
        setDifficulty: function(newDifficulty) {
            this.game.difficulty = newDifficulty;
            this.formatGame();
            return;
        },
        sleep: function(ms = 0) {
            if ( ms === 0 ) {
                ms = Math.floor( Math.random() * 5000 ); //random delay up to 5 s
            }
            const delay = ms;
            return new Promise(resolve => setTimeout(resolve, delay));
        },
        timer: function(command) {  //Takes commands of 'start' and 'stop'
            switch(command) {
                case 'reset': 
                    this.time = {minutes: 0, seconds: 0};
                    return;
                case 'start':
                    this.time = {live: true, minutes: 0, seconds: 0};
                    this.time.clock = setInterval( () => {
                        if (this.time.minutes >= 99 && this.time.seconds >= 59 ) return;
                        if (this.time.seconds >= 59) {
                            this.time.minutes++;
                            this.time.seconds = 0;
                        } else { this.time.seconds++; }
                    }, 1000);
                    return;
                case 'stop':
                    clearInterval(this.time.clock);
                    this.time.live = false;
                    return;
                default: console.log("timer does not accept an arguement of '"+command+"'"); return;
            }
        },
        toggleMobile: function() {
            this.game.mobile = !this.game.mobile;
            this.formatGame();
            return;
        },
        updateMinesRemaining: function() {
            const flagged = this.cells.filter( cell => { return cell.state === 2 } ).length;
            this.minesRemaining = this.grid.mines-flagged;
            return;
        }
    }
}
</script>

<style scoped>
p {
    text-align: center;
}

#board {
    display: inline-grid;
    align-items: stretch;
    border: 5px solid maroon;
    border-radius: 8px;
}
#reset-button {
    visibility: hidden;
    margin-top: 10px;
    border-radius: 10px;

    font-family: 'Verdana';
    font-weight: 600;
}

.button {
    margin-bottom: 0;
    border-radius: 10px;
}
.buttons {
    margin-bottom: 0;
}
.cell {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    user-select: none;

    text-align: center;
    font-family: 'Verdana';
    font-weight: 600;

    background: mediumspringgreen;
    border: 1px solid sandybrown;
    color: transparent;
}
.column {
    padding: 0;
}
.columns:not(:last-child) {
    margin-bottom: 0;
}
.field {
    justify-content: center;
    align-items: center;
    padding: 0.5rem;
}
.field:not(:last-child) {
    margin-bottom: 0;
}
.title {
    margin-bottom: 15px;
}
</style>
